import { Alert, Badge, Button, Card, CardBody, Spinner } from '@foundation/ui'
import { Link, createFileRoute, redirect } from '@tanstack/react-router'
import { type ReactNode, useEffect, useState } from 'react'
import styles from './urls.$id.diff.$changeId.module.css'
import { api } from '@/lib/api'
import { isAuthenticated } from '@/lib/auth-store'
import { toast } from '@/lib/toast'

export const Route = createFileRoute('/urls/$id/diff/$changeId')({
  beforeLoad: () => {
    if (!isAuthenticated()) {
      throw redirect({ to: '/login' })
    }
  },
  component: DiffViewPage,
})

interface ChangeDetail {
  id: string
  changeType: string
  summary?: string
  detectedAt: string
  severity?: string
  beforeData?: Record<string, unknown>
  afterData?: Record<string, unknown>
  url?: string
}

interface DiffEntry {
  key: string
  type: 'added' | 'removed' | 'changed' | 'unchanged'
  before?: unknown
  after?: unknown
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'N/A'
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatChangeType(type: string): string {
  return type.replace(/_/g, ' ')
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '(empty)'
  if (typeof value === 'object') return JSON.stringify(value, null, 2)
  return String(value)
}

function computeDiff(
  before: Record<string, unknown> | undefined,
  after: Record<string, unknown> | undefined
): DiffEntry[] {
  const entries: DiffEntry[] = []
  const allKeys = new Set<string>([...Object.keys(before ?? {}), ...Object.keys(after ?? {})])

  for (const key of allKeys) {
    const bVal = before?.[key]
    const aVal = after?.[key]
    const bStr = JSON.stringify(bVal)
    const aStr = JSON.stringify(aVal)

    if (bVal === undefined && aVal !== undefined) {
      entries.push({ key, type: 'added', after: aVal })
    } else if (bVal !== undefined && aVal === undefined) {
      entries.push({ key, type: 'removed', before: bVal })
    } else if (bStr !== aStr) {
      entries.push({ key, type: 'changed', before: bVal, after: aVal })
    } else {
      entries.push({ key, type: 'unchanged', before: bVal, after: aVal })
    }
  }

  // Sort: changed/added/removed first, then unchanged
  const order: Record<string, number> = { changed: 0, added: 1, removed: 2, unchanged: 3 }
  entries.sort((a, b) => (order[a.type] ?? 3) - (order[b.type] ?? 3))

  return entries
}

function ValueHighlight({ before, after }: { before: unknown; after: unknown }): ReactNode {
  return (
    <span>
      <span className={styles.valueOld ?? ''}>{formatValue(before)}</span>
      <span className={styles.valueArrow ?? ''}>{' \u2192 '}</span>
      <span className={styles.valueNew ?? ''}>{formatValue(after)}</span>
    </span>
  )
}

function DiffViewPage() {
  const { id, changeId } = Route.useParams()
  const [change, setChange] = useState<ChangeDetail | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showUnchanged, setShowUnchanged] = useState(false)

  useEffect(() => {
    const fetchChange = async () => {
      try {
        setIsLoading(true)
        setError(null)
        // Use recent changes endpoint and find the specific change
        const result = await api.getRecentChanges()
        const allChanges = (result.data ?? []) as ChangeDetail[]
        const found = allChanges.find((c) => c.id === changeId)
        if (found) {
          setChange(found)
        } else {
          setError('Change not found')
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load change details'
        setError(message)
        toast.error(message)
      } finally {
        setIsLoading(false)
      }
    }
    void fetchChange()
  }, [changeId])

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="lg" />
        <span>Loading change details...</span>
      </div>
    )
  }

  if (error || !change) {
    return (
      <div className={styles.header}>
        <Link to="/urls/$id" params={{ id }} className={styles.backLink}>
          &larr; Back to URL Details
        </Link>
        <Alert variant="danger">{error ?? 'Change not found'}</Alert>
        <div style={{ marginTop: '1rem' }}>
          <Link to="/urls/$id" params={{ id }}>
            <Button variant="secondary">Go Back</Button>
          </Link>
        </div>
      </div>
    )
  }

  const diff = computeDiff(change.beforeData, change.afterData)
  const filteredDiff = showUnchanged ? diff : diff.filter((d) => d.type !== 'unchanged')
  const hasBeforeAfter = change.beforeData || change.afterData
  const addedCount = diff.filter((d) => d.type === 'added').length
  const removedCount = diff.filter((d) => d.type === 'removed').length
  const changedCount = diff.filter((d) => d.type === 'changed').length

  return (
    <>
      {/* Header */}
      <div className={styles.header}>
        <Link to="/urls/$id" params={{ id }} className={styles.backLink}>
          &larr; Back to URL Details
        </Link>
        <h2 className={styles.title}>Change: {formatChangeType(change.changeType)}</h2>
      </div>

      {/* Metadata */}
      <div className={styles.metadata}>
        <div className={styles.metaItem}>
          <span className={styles.metaLabel}>Detected</span>
          <span className={styles.metaValue}>{formatDate(change.detectedAt)}</span>
        </div>
        <div className={styles.metaItem}>
          <span className={styles.metaLabel}>Type</span>
          <span className={styles.metaValue}>{formatChangeType(change.changeType)}</span>
        </div>
        {change.severity && (
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>Severity</span>
            <Badge
              variant={
                change.severity === 'high'
                  ? 'danger'
                  : change.severity === 'medium'
                    ? 'warning'
                    : 'default'
              }
            >
              {change.severity}
            </Badge>
          </div>
        )}
        {change.summary && (
          <div className={styles.metaItem}>
            <span className={styles.metaLabel}>Summary</span>
            <span className={styles.metaValue}>{change.summary}</span>
          </div>
        )}
      </div>

      {/* Change Summary */}
      {(addedCount > 0 || removedCount > 0 || changedCount > 0) && (
        <Card className={styles.summary}>
          <CardBody>
            <h3 className={styles.summaryTitle}>Changes Summary</h3>
            <ul className={styles.summaryList}>
              {changedCount > 0 && (
                <li className={`${styles.summaryItem} ${styles.summaryModified}`}>
                  {changedCount} field{changedCount !== 1 ? 's' : ''} modified
                </li>
              )}
              {addedCount > 0 && (
                <li className={`${styles.summaryItem} ${styles.summaryAdded}`}>
                  {addedCount} field{addedCount !== 1 ? 's' : ''} added
                </li>
              )}
              {removedCount > 0 && (
                <li className={`${styles.summaryItem} ${styles.summaryRemoved}`}>
                  {removedCount} field{removedCount !== 1 ? 's' : ''} removed
                </li>
              )}
            </ul>
          </CardBody>
        </Card>
      )}

      {/* Diff View */}
      {hasBeforeAfter ? (
        <>
          <div className={styles.diffToolbar ?? ''}>
            <div className={styles.diffCounts ?? ''}>
              {changedCount > 0 && (
                <span className={styles.countModified ?? ''}>{changedCount} modified</span>
              )}
              {addedCount > 0 && (
                <span className={styles.countAdded ?? ''}>{addedCount} added</span>
              )}
              {removedCount > 0 && (
                <span className={styles.countRemoved ?? ''}>{removedCount} removed</span>
              )}
            </div>
            <label className={styles.toggleLabel ?? ''}>
              <input
                type="checkbox"
                checked={showUnchanged}
                onChange={(e) => setShowUnchanged(e.target.checked)}
              />
              Show unchanged fields
            </label>
          </div>
          <div className={styles.diffContainer}>
            {/* Before Panel */}
            <div className={styles.diffPanel}>
              <div className={styles.diffPanelHeaderBefore}>Before</div>
              <div className={styles.diffPanelBody}>
                {filteredDiff.map((entry, index) => {
                  if (entry.type === 'added') return null
                  const rowClass =
                    entry.type === 'removed'
                      ? styles.diffRowRemoved
                      : entry.type === 'changed'
                        ? styles.diffRowChanged
                        : ''
                  return (
                    <div key={entry.key} className={`${styles.diffRow ?? ''} ${rowClass ?? ''}`}>
                      <span className={styles.diffRowNumber ?? ''}>{index + 1}</span>
                      <span className={styles.diffRowKey}>{entry.key}</span>
                      <span className={styles.diffRowValue}>
                        {entry.type === 'changed' ? (
                          <ValueHighlight before={entry.before} after={entry.after} />
                        ) : (
                          formatValue(entry.before)
                        )}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* After Panel */}
            <div className={styles.diffPanel}>
              <div className={styles.diffPanelHeaderAfter}>After</div>
              <div className={styles.diffPanelBody}>
                {filteredDiff.map((entry, index) => {
                  if (entry.type === 'removed') return null
                  const rowClass =
                    entry.type === 'added'
                      ? styles.diffRowAdded
                      : entry.type === 'changed'
                        ? styles.diffRowChanged
                        : ''
                  return (
                    <div key={entry.key} className={`${styles.diffRow ?? ''} ${rowClass ?? ''}`}>
                      <span className={styles.diffRowNumber ?? ''}>{index + 1}</span>
                      <span className={styles.diffRowKey}>{entry.key}</span>
                      <span className={styles.diffRowValue}>
                        {entry.type === 'changed' ? (
                          <ValueHighlight before={entry.before} after={entry.after} />
                        ) : (
                          formatValue(entry.after)
                        )}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </>
      ) : (
        <Card>
          <CardBody>
            <div className={styles.noData}>
              <h3 className={styles.noDataTitle}>No structured diff available</h3>
              <p>
                Detailed before/after data is not available for this change. The change was detected
                as: {formatChangeType(change.changeType)}.
              </p>
            </div>
          </CardBody>
        </Card>
      )}
    </>
  )
}
